import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const BOX = 260;
const OUT = 480;

// Simple drag-to-pan + slider-to-zoom cropper, rendered as its own modal overlay.
// shape="circle" for avatars, shape="square" for cover photos/logos/gallery images.
export default function AvatarCrop({ src, shape = 'circle', onConfirm, onCancel }) {
  const imgElRef = useRef(null);
  const [natural, setNatural] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  useEffect(() => {
    const img = new Image();
    img.onload = () => setNatural({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = src;
    imgElRef.current = img;
  }, [src]);

  if (!natural) {
    return createPortal(
      <div className="modal-backdrop open" data-testid="avatar-crop-modal">
        <div className="modal"><div className="modal-head"><h3>Loading photo…</h3></div></div>
      </div>,
      document.body
    );
  }

  const baseScale = Math.max(BOX / natural.w, BOX / natural.h);
  const scale = baseScale * zoom;
  const dispW = natural.w * scale, dispH = natural.h * scale;
  const maxX = Math.max(0, (dispW - BOX) / 2);
  const maxY = Math.max(0, (dispH - BOX) / 2);
  const clamp = (v, m) => Math.max(-m, Math.min(m, v));

  const onPointerDown = (e) => {
    dragRef.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.x, dy = e.clientY - dragRef.current.y;
    setOffset({ x: clamp(dragRef.current.ox + dx, maxX), y: clamp(dragRef.current.oy + dy, maxY) });
  };
  const onPointerUp = () => { dragRef.current = null; };

  const confirm = () => {
    const canvas = document.createElement('canvas');
    canvas.width = OUT; canvas.height = OUT;
    const ctx = canvas.getContext('2d');
    const outScale = OUT / BOX;
    const outDispW = dispW * outScale, outDispH = dispH * outScale;
    const dx = (OUT - outDispW) / 2 + offset.x * outScale;
    const dy = (OUT - outDispH) / 2 + offset.y * outScale;
    ctx.drawImage(imgElRef.current, dx, dy, outDispW, outDispH);
    onConfirm(canvas.toDataURL('image/jpeg', 0.85));
  };

  return createPortal(
    <div className="modal-backdrop open" data-testid="avatar-crop-modal">
      <div className="modal avatar-crop-modal">
        <div className="modal-head"><h3>Position your photo</h3><button type="button" className="close" onClick={onCancel} data-testid="avatar-crop-close">×</button></div>
        <div className="avatar-crop">
          <div
            className={`avatar-crop-box ${shape === 'circle' ? 'circle' : ''}`}
            style={{ width: BOX, height: BOX }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            data-testid="avatar-crop-canvas"
          >
            <img
              src={src}
              alt="Crop preview"
              draggable={false}
              style={{ width: dispW, height: dispH, maxWidth: 'none', maxHeight: 'none', left: (BOX - dispW) / 2 + offset.x, top: (BOX - dispH) / 2 + offset.y }}
            />
          </div>
          <label className="avatar-crop-zoom">
            <span>Zoom</span>
            <input type="range" min="1" max="3" step="0.01" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} data-testid="avatar-crop-zoom-slider" />
          </label>
          <p className="avatar-crop-hint">Drag the photo to reposition it{shape === 'circle' ? ' inside the circle' : ''}, use the slider to zoom in or out.</p>
          <div className="avatar-crop-actions">
            <button type="button" className="btn secondary" onClick={onCancel} data-testid="avatar-crop-cancel">Cancel</button>
            <button type="button" className="btn" onClick={confirm} data-testid="avatar-crop-save">Use this photo</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
